import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { env } from '../../environments/environment';
import { AuthService } from '../shared/auth.service';
import { Answer, Flag, FlagGroup, ListFilters, ListResults, Setting } from '../shared/interfaces';

@Injectable({
  providedIn: 'root',
})
export class FlagService {
  violationRequiredFields = [
    'elevatedRisk',
    'deadline',
    'whyDoesThisMatter',
    'actionRequested',
    'documentationRequested',
  ];

  discrepancyRequiredFields = ['stated', 'actual', 'resultOfTheFinding', 'impact', 'effectiveDate'];

  typesCache: Promise<any[]>;
  subtypeInfo: Promise<Setting[]>;

  constructor(
    protected http: HttpClient,
    protected auth: AuthService,
  ) {
    auth.isAuthenticated.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.subtypeInfo = http.get<Setting[]>(`${env.APIUrl}/setting/flagSubtypeInfo`).toPromise();
      }
    });
  }

  private async getTypesWitSubtypes() {
    return new Promise<any[]>(resolve => {
      this.auth.isAuthenticated.subscribe(isAuthenticated => {
        if (isAuthenticated) {
          resolve(this.http.get<any[]>(`${env.APIUrl}/flagtype/withsubtypes`).toPromise());
        } else {
          resolve([]);
        }
      });
    });
  }

  list(filters?: ListFilters) {
    return this.http.get<ListResults<Flag>>(`${env.APIUrl}/flag`, { params: filters as any });
  }

  get(id: number) {
    return this.http.get<Flag>(`${env.APIUrl}/flag/${id}`).toPromise();
  }

  edit(id: number, flag: Flag) {
    return this.http.put<Answer>(`${env.APIUrl}/flag/${id}`, flag).toPromise();
  }

  create(flag: Flag) {
    return this.http.post<Flag>(`${env.APIUrl}/flag`, flag).toPromise();
  }

  remove(id: number) {
    return this.http.delete<Answer>(`${env.APIUrl}/flag/${id}`).toPromise();
  }

  move(flagId: number, newPosition: number, targetGroupId?: number) {
    return this.http.post<Flag>(`${env.APIUrl}/flag/${flagId}/move`, { newPosition, targetGroupId }).toPromise();
  }

  reorder(flagId: number, newPosition: number, groupId: number) {
    return this.http.put<Flag>(`${env.APIUrl}/flag/${flagId}/reorder`, { newPosition, groupId }).toPromise();
  }

  listTypes() {
    if (!this.typesCache) {
      this.typesCache = this.getTypesWitSubtypes();
    }
    return this.typesCache;
  }

  createGroup(flags: Array<Partial<Flag>>) {
    return this.http.post<any>(`${env.APIUrl}/flaggroup`, { flags }).toPromise();
  }

  addToGroup(groupId: number, flagId: number) {
    return this.http.put<any>(`${env.APIUrl}/flag/${flagId}/group/${groupId}`, null).toPromise();
  }

  addComment(flagId: number, comment: { text: string }) {
    return this.http.put<Flag>(`${env.APIUrl}/flag/${flagId}/addcomment`, comment).toPromise();
  }

  removeComment(flagId: number, index: number) {
    return this.http.put<Flag>(`${env.APIUrl}/flag/${flagId}/removecomment/${index}`, null).toPromise();
  }

  uploadPicture(formData) {
    return this.http.post<any>(`${env.APIUrl}/image/upload`, formData).toPromise();
  }

  changeFlagsStatus(data) {
    return this.http.post<any>(`${env.APIUrl}/flag/changeFlagsStatus`, { ...data }).toPromise();
  }

  setFlagsDeadline(data) {
    return this.http.post<any>(`${env.APIUrl}/flag/setFlagsDeadline`, { ...data }).toPromise();
  }

  isCompleted(flagOrGroup: Flag | FlagGroup) {
    // Flag is ignored
    if (flagOrGroup && (flagOrGroup as Flag).ignore) {
      return true;
    }

    // All flags from the group are ignored
    if (flagOrGroup && (flagOrGroup as FlagGroup).flags) {
      const flags = (flagOrGroup as FlagGroup).flags;
      if (flags.filter(f => f.ignore).length === flags.length) {
        return true;
      }
    }

    return this.isFilled(flagOrGroup);
  }

  isFilled(flagOrGroup: Flag | FlagGroup) {
    const values = [];
    const type = flagOrGroup.subtype && flagOrGroup.subtype.type && flagOrGroup.subtype.type.name;

    switch (type) {
      case 'Discrepancy':
        values.push(...this.discrepancyRequiredFields.map(key => flagOrGroup.discrepancy[key]));
        break;

      case 'Violation':
        values.push(...this.violationRequiredFields.map(key => flagOrGroup.violation[key]));
        break;

      default:
        return false;
    }

    return values.findIndex(x => !x) === -1;
  }

  getSubtypeInfo(infoArray, subtypeName: string) {
    if (!infoArray) {
      return { name: subtypeName, color: 'black' };
    }
    let subtype = subtypeName;
    if (!infoArray.find(i => i.key === `${subtypeName}_name`) && infoArray.find(a => a.value === subtypeName)) {
      subtype = infoArray.find(a => a.value === subtypeName).key.replace('_name', '');
    }
    const nameSetting = infoArray.find(i => i.key === `${subtype}_name`);
    const colorSetting = infoArray.find(i => i.key === `${subtype}_color`);
    return {
      name: nameSetting?.value,
      color: colorSetting?.value,
    };
  }
}
